import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import wingitDesign from './designSystem';
import {
  fetchOfficialTechLogsForBookings,
  fetchPreviousFlightMeterReading,
  indexOfficialTechLogs,
  closeFlightWithOfficialTechLog,
} from './officialTechLog';
import { isLocalTestSimulationEnabled } from './preflight/localTestSimulation';
import {
  getBookingUserIdCandidates,
  isPostFlightCompleted,
  selectPostFlightBooking,
} from './booking/activeFlightSelection';
import './pilotPagesMobile.css';

function isMissingBookingColumnsError(error) {
  const message = String(
    error?.message || ''
  ).toLowerCase();

  return (
    message.includes('schema cache') ||
    message.includes('column') ||
    message.includes('end_date') ||
    message.includes('end_time') ||
    message.includes('departure_airfield') ||
    message.includes('destination_airfield') ||
    message.includes('tech_log_status')
  );
}

function parseBookingStart(booking) {
  const date = String(
    booking?.date || ''
  ).trim();

  const time =
    String(booking?.time || '').trim() ||
    '00:00';

  if (!date) return null;

  const parsed = new Date(
    `${date}T${time}`
  );

  return Number.isFinite(parsed.getTime())
    ? parsed
    : null;
}

function parseBookingEnd(booking) {
  const start = parseBookingStart(booking);

  if (!start) return null;

  const endDate = String(
    booking?.end_date || ''
  ).trim();

  const endTime = String(
    booking?.end_time || ''
  ).trim();

  if (endDate) {
    const parsed = new Date(
      `${endDate}T${endTime || '23:59'}`
    );

    if (
      Number.isFinite(parsed.getTime()) &&
      parsed > start
    ) {
      return parsed;
    }
  }

  const durationMinutes = Number(
    booking?.duration
  );

  if (
    Number.isFinite(durationMinutes) &&
    durationMinutes > 0
  ) {
    return new Date(
      start.getTime() +
        durationMinutes * 60000
    );
  }

  return new Date(
    start.getTime() + 60 * 60000
  );
}

function formatDate(value) {
  if (!value) return 'Unknown';

  const date =
    value instanceof Date
      ? value
      : new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return 'Unknown';
  }

  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(value) {
  if (!value) return 'Unknown';

  const date =
    value instanceof Date
      ? value
      : new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return 'Unknown';
  }

  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateTime(value) {
  if (!value) return 'Unknown';

  const date =
    value instanceof Date
      ? value
      : new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return 'Unknown';
  }

  return `${formatDate(date)} at ${formatTime(date)}`;
}

function toClockValue(date) {
  if (
    !date ||
    !Number.isFinite(date.getTime())
  ) {
    return '';
  }

  const hours = String(
    date.getHours()
  ).padStart(2, '0');

  const minutes = String(
    date.getMinutes()
  ).padStart(2, '0');

  return `${hours}:${minutes}`;
}

function getDepartureAirfield(booking) {
  const briefing =
    booking?.briefing_data &&
    typeof booking.briefing_data ===
      'object'
      ? booking.briefing_data
      : {};

  return String(
    booking?.departure_airfield ||
      briefing.departureAirfield ||
      briefing.departure_airfield ||
      'Not recorded'
  )
    .trim()
    .toUpperCase();
}

function getArrivalAirfield(booking) {
  const briefing =
    booking?.briefing_data &&
    typeof booking.briefing_data ===
      'object'
      ? booking.briefing_data
      : {};

  return String(
    booking?.destination_airfield ||
      briefing.destinationAirfield ||
      briefing.destination_airfield ||
      'Not recorded'
  )
    .trim()
    .toUpperCase();
}

function getReadingLabel(method) {
  const normalized = String(
    method || ''
  )
    .trim()
    .toLowerCase();

  if (normalized.includes('hobbs')) {
    return 'Hobbs';
  }

  if (
    normalized.includes('tacho') ||
    normalized.includes('tach')
  ) {
    return 'Tacho';
  }

  if (normalized.includes('datcon')) {
    return 'Datcon';
  }

  return 'Meter';
}

function getBriefingData(booking) {
  return booking?.briefing_data &&
    typeof booking.briefing_data === 'object'
    ? booking.briefing_data
    : {};
}

function getSavedPilotSubmission(booking) {
  const briefing = getBriefingData(booking);
  const submission = briefing.techLogSubmission;

  return submission && typeof submission === 'object'
    ? submission
    : {};
}

function hasRecordedDefect(entry) {
  const defect = String(
    entry?.defect || entry?.snag || ''
  )
    .trim()
    .toLowerCase();

  return Boolean(defect && defect !== 'nil');
}

export function getPostFlightMode(booking) {
  return isPostFlightCompleted(booking)
    ? 'read_only'
    : 'editable';
}

function formatPostFlightStatus(status) {
  return String(status || '')
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    ) || 'Unknown';
}

function aircraftUsesMeter(method) {
  const normalized = String(
    method || ''
  )
    .trim()
    .toLowerCase();

  return Boolean(
    normalized &&
      normalized !== 'none' &&
      normalized !== 'not recorded'
  );
}

function calculateElapsedTime(
  departureTime,
  arrivalTime
) {
  if (!departureTime || !arrivalTime) {
    return '';
  }

  const [
    departureHour,
    departureMinute,
  ] = departureTime
    .split(':')
    .map(Number);

  const [
    arrivalHour,
    arrivalMinute,
  ] = arrivalTime
    .split(':')
    .map(Number);

  if (
    !Number.isFinite(departureHour) ||
    !Number.isFinite(departureMinute) ||
    !Number.isFinite(arrivalHour) ||
    !Number.isFinite(arrivalMinute)
  ) {
    return '';
  }

  const departureTotal =
    departureHour * 60 +
    departureMinute;

  let arrivalTotal =
    arrivalHour * 60 +
    arrivalMinute;

  if (arrivalTotal < departureTotal) {
    arrivalTotal += 24 * 60;
  }

  const elapsedMinutes =
    arrivalTotal - departureTotal;

  const hours = Math.floor(
    elapsedMinutes / 60
  );

  const minutes =
    elapsedMinutes % 60;

  return `${hours}:${String(
    minutes
  ).padStart(2, '0')}`;
}

function numericValue(value) {
  const text = String(value || '').trim();

  if (!text) return null;

  const number = Number(text);

  return Number.isFinite(number)
    ? number
    : null;
}

function readingsMatch(
  currentReading,
  previousReading
) {
  const current =
    numericValue(currentReading);

  const previous =
    numericValue(previousReading);

  if (
    current === null ||
    previous === null
  ) {
    return true;
  }

  return Math.abs(current - previous) < 0.11;
}

function createExceptionReasons({
  usesMeter,
  startReading,
  endReading,
  previousEndReading,
  noDefects,
  defect,
}) {
  const reasons = [];

  const start =
    numericValue(startReading);

  const finish =
    numericValue(endReading);

  if (
    usesMeter &&
    (start === null || finish === null)
  ) {
    reasons.push(
      'Meter reading incomplete'
    );
  }

  if (
    start !== null &&
    finish !== null &&
    finish < start
  ) {
    reasons.push(
      'Finish reading is lower than start reading'
    );
  }

  if (
    previousEndReading !== null &&
    start !== null &&
    !readingsMatch(
      startReading,
      previousEndReading
    )
  ) {
    reasons.push(
      'Start reading does not match the previous recorded finish reading'
    );
  }

  if (
    !noDefects &&
    String(defect || '').trim()
  ) {
    reasons.push('Defect reported');
  }

  return reasons;
}

export default function PostFlightPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const requestedBookingId = String(
    searchParams.get('bookingId') || ''
  ).trim();
  const isLocalTestSimulation =
    isLocalTestSimulationEnabled(
      searchParams,
      process.env.NODE_ENV
    );

  const session = useSession();
  const user = session?.user;
  const ds = wingitDesign;

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [loadError, setLoadError] =
    useState('');

  const [saveError, setSaveError] =
    useState('');

  const [booking, setBooking] =
    useState(null);

  const [aircraft, setAircraft] =
    useState(null);

  const [
    previousEntry,
    setPreviousEntry,
  ] = useState(null);
  const [
    previousReadingError,
    setPreviousReadingError,
  ] = useState('');

  const [startReading, setStartReading] =
    useState('');

  const [endReading, setEndReading] =
    useState('');

  const [
    actualDepartureTime,
    setActualDepartureTime,
  ] = useState('');

  const [
    actualArrivalTime,
    setActualArrivalTime,
  ] = useState('');

  const [
    actualDepartureAirfield,
    setActualDepartureAirfield,
  ] = useState('');

  const [
    actualArrivalAirfield,
    setActualArrivalAirfield,
  ] = useState('');

  const [fuelExpanded, setFuelExpanded] =
    useState(false);

  const [fuelAdded, setFuelAdded] =
    useState('');

  const [oilExpanded, setOilExpanded] =
    useState(false);

  const [oilAdded, setOilAdded] =
    useState('');

  const [noDefects, setNoDefects] =
    useState(true);

  const [defect, setDefect] =
    useState('');

  const [noteExpanded, setNoteExpanded] =
    useState(false);

  const [remarks, setRemarks] =
    useState('');
  const [noFlightModalOpen, setNoFlightModalOpen] =
    useState(false);
  const [noFlightSaving, setNoFlightSaving] =
    useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadPreviousEntry(
      selectedBooking,
      savedSubmission
    ) {
      const { data, error } =
        await fetchPreviousFlightMeterReading(
          selectedBooking.id
        );

      if (cancelled) {
        return;
      }

      if (error || data?.ok !== true) {
        setPreviousReadingError(
          error?.message ||
            data?.message ||
            'Could not verify the previous meter reading.'
        );
        return;
      }

      const previousReading =
        numericValue(data.reading);

      if (previousReading === null) {
        return;
      }

      setPreviousEntry({
        entry: { endReading: previousReading },
      });

      if (
        !String(
          savedSubmission?.startReading || ''
        ).trim() &&
        previousReading !== null &&
        previousReading !== undefined &&
        previousReading !== ''
      ) {
        setStartReading(
          String(previousReading)
        );
      }
    }

    async function loadFlight() {
      if (!user?.id) {
        if (!cancelled) {
          setBooking(null);
          setAircraft(null);
          setLoading(false);
        }

        return;
      }

      setLoading(true);
      setLoadError('');
      setPreviousReadingError('');
      setPreviousEntry(null);

      const bookingUserIdCandidates = await getBookingUserIdCandidates(
        supabase,
        user.id
      );

      if (bookingUserIdCandidates.length === 0) {
        setBooking(null);
        setAircraft(null);
        setLoading(false);
        return;
      }

      let query = supabase
        .from('bookings')
        .select(
          'id, user_id, aircraft_id, date, time, duration, end_date, end_time, briefing_data, tech_log_status, tech_log_completed_at'
        )
        .in('user_id', bookingUserIdCandidates);

      if (requestedBookingId) {
        query = query.eq(
          'id',
          requestedBookingId
        );
      }

      let { data, error } = await query
        .order('date', {
          ascending: false,
        })
        .order('time', {
          ascending: false,
        })
        .limit(
          requestedBookingId ? 1 : 30
        );

      if (
        error &&
        isMissingBookingColumnsError(error)
      ) {
        let fallbackQuery = supabase
          .from('bookings')
          .select(
            'id, user_id, aircraft_id, date, time, duration, briefing_data, tech_log_status, tech_log_completed_at'
          )
          .in('user_id', bookingUserIdCandidates);

        if (requestedBookingId) {
          fallbackQuery =
            fallbackQuery.eq(
              'id',
              requestedBookingId
            );
        }

        const fallback =
          await fallbackQuery
            .order('date', {
              ascending: false,
            })
            .order('time', {
              ascending: false,
            })
            .limit(
              requestedBookingId ? 1 : 30
            );

        data = (
          fallback.data || []
        ).map((row) => ({
          ...row,
          end_date: null,
          end_time: null,
          // preserve real completion status when the column is actually available
          tech_log_status: row?.tech_log_status || '',
          tech_log_completed_at: row?.tech_log_completed_at || null,
        }));

        error = fallback.error;
      }

      if (cancelled) return;

      if (error) {
        setBooking(null);
        setAircraft(null);

        setLoadError(
          'Could not load the selected flight.'
        );

        setLoading(false);
        return;
      }

      const selected = selectPostFlightBooking(
        data || [],
        requestedBookingId,
        new Date()
      );

      if (!selected) {
        setBooking(null);
        setAircraft(null);

        if (requestedBookingId) {
          setLoadError(
            'This flight is not available for closure.'
          );
        }

        setLoading(false);
        return;
      }

      const {
        data: aircraftRow,
        error: aircraftError,
      } = await supabase
        .from('aircraft')
        .select(
          'id, registration, icao_type, recording_method'
        )
        .eq(
          'id',
          selected.aircraft_id
        )
        .maybeSingle();

      if (cancelled) return;

      setBooking(selected);

      setAircraft(
        aircraftError
          ? null
          : aircraftRow || null
      );

      const savedSubmission =
        getSavedPilotSubmission(selected);

      const plannedStart =
        parseBookingStart(selected);

      const plannedEnd =
        parseBookingEnd(selected);

      const savedDepartureTime = String(
        savedSubmission.departureTime || ''
      ).trim();

      const savedArrivalTime = String(
        savedSubmission.arrivalTime || ''
      ).trim();

      const savedDepartureAirfield = String(
        savedSubmission.departureAirfield || ''
      ).trim().toUpperCase();

      const savedArrivalAirfield = String(
        savedSubmission.arrivalAirfield || ''
      ).trim().toUpperCase();

      const savedStartReading = String(
        savedSubmission.startReading || ''
      ).trim();

      const savedEndReading = String(
        savedSubmission.endReading || ''
      ).trim();

      const savedFuel = String(
        savedSubmission.fuelUpliftLitres || ''
      ).trim();

      const savedOil = String(
        savedSubmission.oilUpliftLitres || ''
      ).trim();

      const savedDefect = String(
        savedSubmission.defect ||
          savedSubmission.snag ||
          ''
      ).trim();

      const savedRemarks = String(
        savedSubmission.remarks ||
          savedSubmission.notes ||
          ''
      ).trim();

      const hasSavedDefect =
        savedDefect &&
        savedDefect.toLowerCase() !== 'nil';

      const savedServiceable =
        savedSubmission.serviceable === false
          ? false
          : !hasSavedDefect &&
            String(
              savedSubmission.aircraftStatus || ''
            )
              .trim()
              .toLowerCase() !== 'defect_reported';

      setActualDepartureTime(
        savedDepartureTime ||
          (plannedStart
            ? toClockValue(plannedStart)
            : '')
      );

      setActualArrivalTime(
        savedArrivalTime ||
          (plannedEnd
            ? toClockValue(plannedEnd)
            : '')
      );

      const plannedDepartureAirfield =
        getDepartureAirfield(selected);

      const plannedArrivalAirfield =
        getArrivalAirfield(selected);

      setActualDepartureAirfield(
        savedDepartureAirfield ||
          (plannedDepartureAirfield === 'NOT RECORDED'
            ? ''
            : plannedDepartureAirfield)
      );

      setActualArrivalAirfield(
        savedArrivalAirfield ||
          (plannedArrivalAirfield === 'NOT RECORDED'
            ? ''
            : plannedArrivalAirfield)
      );

      setStartReading(savedStartReading);
      setEndReading(savedEndReading);
      setFuelAdded(savedFuel);
      setFuelExpanded(
        Boolean(savedFuel && savedFuel !== '0')
      );
      setOilAdded(savedOil);
      setOilExpanded(
        Boolean(savedOil && savedOil !== '0')
      );
      setNoDefects(savedServiceable);
      setDefect(hasSavedDefect ? savedDefect : '');
      setRemarks(savedRemarks);
      setNoteExpanded(Boolean(savedRemarks));

      await loadPreviousEntry(
        selected,
        savedSubmission
      );

      if (!cancelled) {
        setLoading(false);
      }
    }

    loadFlight();

    return () => {
      cancelled = true;
    };
  }, [user?.id, requestedBookingId]);

  const plannedStart = useMemo(
    () => parseBookingStart(booking),
    [booking]
  );

  const plannedEnd = useMemo(
    () => parseBookingEnd(booking),
    [booking]
  );

  const flightTime = useMemo(
    () =>
      calculateElapsedTime(
        actualDepartureTime,
        actualArrivalTime
      ),
    [
      actualDepartureTime,
      actualArrivalTime,
    ]
  );

  const postFlightMode = useMemo(
    () => getPostFlightMode(booking),
    [booking]
  );

  const isReadOnlyMode =
    postFlightMode === 'read_only';

  const savedSubmission = useMemo(
    () => getSavedPilotSubmission(booking),
    [booking]
  );
  const hasSimulationOnlyPreflight =
    booking?.briefing_data?.preflight?.simulationOnly === true;
  const isSyntheticSimulationCloseout =
    hasSimulationOnlyPreflight &&
    isLocalTestSimulation;

  const aircraftLabel = aircraft
    ? [
        aircraft.registration,
        aircraft.icao_type
          ? `(${aircraft.icao_type})`
          : '',
      ]
        .filter(Boolean)
        .join(' ')
    : booking
      ? `Aircraft ${booking.aircraft_id}`
      : 'No aircraft';

  const readingLabel =
    getReadingLabel(
      aircraft?.recording_method
    );

  const usesMeter =
    aircraftUsesMeter(
      aircraft?.recording_method
    );

  const hasSavedReadingData =
    Boolean(startReading) || Boolean(endReading);

  const savedAircraftCondition = noDefects
    ? 'Serviceable'
    : 'Defect reported';

  const postFlightStatusLabel =
    formatPostFlightStatus(
      booking?.tech_log_status
    );
  const isDidNotFly =
    String(booking?.tech_log_status || '')
      .trim()
      .toLowerCase() === 'did_not_fly';

  const previousEndReading =
    previousEntry
      ? numericValue(
          previousEntry.entry
            ?.endReading ??
            previousEntry.entry
              ?.endMeterReading
        )
      : null;

  const readingDiscrepancy =
    previousEndReading !== null &&
    numericValue(startReading) !== null &&
    !readingsMatch(
      startReading,
      previousEndReading
    );

  const endBeforeStart =
    numericValue(startReading) !== null &&
    numericValue(endReading) !== null &&
    numericValue(endReading) <
      numericValue(startReading);

  const canSubmit =
    (!hasSimulationOnlyPreflight ||
      isLocalTestSimulation) &&
    Boolean(actualDepartureTime) &&
    Boolean(actualArrivalTime) &&
    (!usesMeter ||
      (numericValue(startReading) !==
        null &&
        numericValue(endReading) !==
          null)) &&
    !endBeforeStart &&
    (noDefects ||
      Boolean(defect.trim()));

  async function handleSubmit() {
    if (
      !booking ||
      !user?.id ||
      saving
    ) {
      return;
    }

    if (isReadOnlyMode) {
      setSaveError(
        'This flight is already closed and cannot be submitted again.'
      );

      return;
    }

    if (
      hasSimulationOnlyPreflight &&
      !isLocalTestSimulation
    ) {
      setSaveError(
        'This synthetic pre-flight can only be closed in local development simulation mode.'
      );

      return;
    }

    if (!canSubmit) {
      setSaveError(
        'Please complete the required flight details before closing the flight.'
      );

      return;
    }

    setSaving(true);
    setSaveError('');

    const exceptionReasons =
      createExceptionReasons({
        usesMeter,
        startReading,
        endReading,
        previousEndReading,
        noDefects,
        defect,
      });

    const needsAttention =
      exceptionReasons.length > 0;

    const defectReported =
      !noDefects &&
      Boolean(defect.trim());

    const submittedAt =
      new Date().toISOString();
    const simulationLabel =
      'LOCAL TEST ONLY - synthetic software validation; no actual flight occurred. Aircraft condition, times, readings and route are not operational evidence.';
    const submissionRemarks = [
      isSyntheticSimulationCloseout
        ? simulationLabel
        : '',
      noteExpanded ? remarks.trim() : '',
    ]
      .filter(Boolean)
      .join(' ');

    const pilotName =
      session?.profile?.full_name ||
      user?.user_metadata?.full_name ||
      user?.email ||
      'Unknown pilot';

    const entry = {
      flightNumber:
        String(booking.id),

      bookingId:
        String(booking.id),

      aircraftId:
        String(
          booking.aircraft_id || ''
        ),

      pilotId: String(user.id),
      pilotName,

      flightDate:
        booking.date || '',

      departureAirfield:
        String(actualDepartureAirfield || getDepartureAirfield(booking))
          .trim()
          .toUpperCase(),

      arrivalAirfield:
        String(actualArrivalAirfield || getArrivalAirfield(booking))
          .trim()
          .toUpperCase(),

      departureTime:
        actualDepartureTime,

      arrivalTime:
        actualArrivalTime,

      flightTime,

      startReading:
        startReading.trim(),

      endReading:
        endReading.trim(),

      recordingMethod:
        aircraft?.recording_method ||
        '',

      fuelUpliftLitres:
        fuelExpanded
          ? fuelAdded.trim()
          : '0',

      oilUpliftLitres:
        oilExpanded
          ? oilAdded.trim()
          : '0',

      aircraftStatus:
        defectReported
          ? 'defect_reported'
          : 'serviceable',

      serviceable:
        !defectReported,

      defect:
        defectReported
          ? defect.trim()
          : 'Nil',

      snag:
        defectReported
          ? defect.trim()
          : 'Nil',

      defectStatus:
        defectReported
          ? 'open'
          : 'none',

      defectReportedAt:
        defectReported
          ? submittedAt
          : '',

      defectReportedBy:
        defectReported
          ? pilotName
          : '',

      remarks:
        submissionRemarks,

      notes:
        submissionRemarks,

      signature: pilotName,
      submittedAt,

      reviewStatus:
        needsAttention
          ? 'attention_required'
          : 'complete',

      exceptionReasons,
      ...(isSyntheticSimulationCloseout
        ? {
            simulationOnly: true,
            simulationLabel,
          }
        : {}),
    };

    try {
      let expectedVersion = null;
      const bookingKey = String(booking.id);

      if (!needsAttention) {
        const {
          data: existingRows,
          error: techLogError,
        } = await fetchOfficialTechLogsForBookings([bookingKey]);

        if (techLogError) {
          setSaveError(
            'Wingit could not check the current technical log version. Please reload and try again.'
          );
          setSaving(false);
          return;
        }

        const indexed = indexOfficialTechLogs(existingRows || []);
        expectedVersion =
          indexed.versionMap?.[bookingKey] ?? 0;
      }

      const result = await closeFlightWithOfficialTechLog({
        bookingId: bookingKey,
        entry,
        expectedStatus: booking.tech_log_status || '',
        expectedVersion,
      });

      if (!result.ok) {
        setSaveError(
          result.message ||
            'Wingit could not close the flight. Please reload and try again.'
        );
        setSaving(false);
        return;
      }

      navigate('/dashboard', {
        replace: true,
      });
    } catch (_error) {
      setSaveError(
        'Wingit could not close the flight. Please try again.'
      );
      setSaving(false);
    }
  }

  async function handleDidNotFlyConfirm() {
    if (!booking || !user?.id || noFlightSaving) {
      return;
    }

    setNoFlightSaving(true);
    setSaveError('');

    try {
      const response = await fetch('/.netlify/functions/close-flight-as-did-not-fly', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {}),
        },
        body: JSON.stringify({ bookingId: booking.id }),
      });

      if (!response.ok) {
        setSaveError(
          'Wingit could not mark this booking as did not fly. Please try again.'
        );
        return;
      }

      setNoFlightModalOpen(false);

      navigate('/dashboard', {
        replace: true,
      });
    } catch (_error) {
      setSaveError(
        'Wingit could not mark this booking as did not fly. Please try again.'
      );
    } finally {
      setNoFlightSaving(false);
    }
  }

  return (
    <div className="pilot-page pilot-post-flight" style={styles.page}>
      <div className="pilot-post-flight__header" style={styles.header}>
        <div>
          <h1 style={styles.title}>
            {isReadOnlyMode
              ? 'Flight details'
              : 'Close flight'}
          </h1>

          <div style={styles.subtitle}>
            {isReadOnlyMode
              ? 'Review the saved post-flight information for this flight.'
              : 'Confirm the completed flight and record anything that changed.'}
          </div>
        </div>

        <button
          type="button"
          onClick={() =>
            navigate('/dashboard')
          }
          style={ds.buttons.secondary}
        >
          Back
        </button>
      </div>

      {loading ? (
        <div style={styles.messageCard}>
          Loading your flight...
        </div>
      ) : null}

      {!loading && loadError ? (
        <div style={styles.errorCard}>
          {loadError}
        </div>
      ) : null}

      {!loading &&
      !loadError &&
      !booking ? (
        <div style={styles.messageCard}>
          <div style={styles.noFlightTitle}>
            {requestedBookingId
              ? 'Flight unavailable'
              : 'No flight awaiting closure'}
          </div>

          <div style={styles.noFlightText}>
            {requestedBookingId
              ? 'This flight is not available for post-flight access.'
              : 'There are no completed flights requiring a post-flight entry.'}
          </div>
        </div>
      ) : null}

      {!loading &&
      !loadError &&
      booking ? (
        <>
          {hasSimulationOnlyPreflight ? (
            <div
              role="status"
              style={styles.notice}
            >
              <strong>
                LOCAL TEST ONLY — SYNTHETIC FLIGHT SIMULATION
              </strong>
              <div>
                This booking contains simulated pre-flight data. No actual
                flight occurred; this closeout and its meter values are
                software-test data, not operational evidence.
              </div>
            </div>
          ) : null}

          <section className="pilot-post-flight__card" style={styles.summaryCard}>
            <div className="pilot-post-flight__summary" style={styles.aircraftSummary}>
              <div>
                <div
                  style={
                    styles.aircraftRegistration
                  }
                >
                  {aircraftLabel}
                </div>

                <div
                  style={
                    styles.aircraftRoute
                  }
                >
                  {getDepartureAirfield(
                    booking
                  )}
                  {' → '}
                  {getArrivalAirfield(
                    booking
                  )}
                </div>
              </div>

              <div style={styles.bookingSummary}>
                <div style={styles.bookingDate}>
                  {formatDate(plannedStart)}
                </div>

                <div style={styles.bookingTime}>
                  Booked{' '}
                  {formatTime(plannedStart)}
                  {' – '}
                  {formatTime(plannedEnd)}
                </div>
              </div>
            </div>
          </section>

          {!isReadOnlyMode && usesMeter ? (
            <section
              style={
                readingDiscrepancy
                  ? styles.continuityWarning
                  : styles.continuityCard
              }
            >
              <div
                style={styles.continuityTitle}
              >
                Previous flight
              </div>

              <div
                style={styles.continuityText}
              >
                {previousEndReading !== null
                  ? `Last recorded ${readingLabel} Finish: ${previousEndReading}. Confirm that it matches the aircraft.`
                  : previousReadingError
                    ? `Could not verify the previous ${readingLabel} reading. ${previousReadingError} Enter the aircraft's current reading below.`
                    : `No previous ${readingLabel} reading is available. Enter the aircraft's current reading below.`}
              </div>

              {readingDiscrepancy ? (
                <div style={styles.warningText}>
                  The current {readingLabel}{' '}
                  Start does not match the last
                  recorded finish. Wingit will
                  flag this for attention.
                </div>
              ) : null}
            </section>
          ) : null}

          {isReadOnlyMode ? (
            <>
              <section style={styles.statusCard}>
                <div style={styles.statusHeader}>
                  <div>
                    <div style={styles.statusKicker}>
                      Post-flight status
                    </div>

                    <div style={styles.statusTitle}>
                      {postFlightStatusLabel || 'Completed'}
                    </div>

                    <div style={styles.statusMeta}>
                      {isDidNotFly
                        ? 'This booking was closed without a flight taking place.'
                        : booking?.tech_log_completed_at
                          ? `Saved ${formatDateTime(
                              booking.tech_log_completed_at
                            )}`
                          : 'This flight has been closed.'}
                    </div>
                  </div>

                  <div style={styles.completedBadge}>
                    {postFlightStatusLabel}
                  </div>
                </div>
              </section>

              <section style={styles.formCard}>
                <h2 style={styles.sectionHeading}>
                  Flight details
                </h2>

                <div className="pilot-post-flight__detail-grid" style={styles.detailGrid}>
                  <ReadOnlyItem
                    label="Actual departure"
                    value={
                      actualDepartureTime ||
                      'Not recorded'
                    }
                  />
                  <ReadOnlyItem
                    label="Actual arrival"
                    value={
                      actualArrivalTime ||
                      'Not recorded'
                    }
                  />
                  <ReadOnlyItem
                    label="Actual departure airfield"
                    value={
                      actualDepartureAirfield ||
                      'Not recorded'
                    }
                  />
                  <ReadOnlyItem
                    label="Actual arrival airfield"
                    value={
                      actualArrivalAirfield ||
                      'Not recorded'
                    }
                  />
                  <ReadOnlyItem
                    label="Flight time"
                    value={
                      savedSubmission.flightTime ||
                      flightTime ||
                      'Not recorded'
                    }
                  />
                  <ReadOnlyItem
                    label="Recording method"
                    value={
                      String(
                        savedSubmission.recordingMethod ||
                          aircraft?.recording_method ||
                          'Not recorded'
                      ).toUpperCase()
                    }
                  />
                  <ReadOnlyItem
                    label="Pilot signature"
                    value={
                      savedSubmission.signature ||
                      'Not recorded'
                    }
                  />
                  <ReadOnlyItem
                    label="Completion status"
                    value={postFlightStatusLabel}
                  />
                </div>
              </section>

              {usesMeter || hasSavedReadingData ? (
                <section style={styles.formCard}>
                  <h2 style={styles.sectionHeading}>
                    Meter readings
                  </h2>

                  <div className="pilot-post-flight__detail-grid" style={styles.detailGrid}>
                    <ReadOnlyItem
                      label={`${readingLabel} Start`}
                      value={
                        startReading ||
                        'Not recorded'
                      }
                    />
                    <ReadOnlyItem
                      label={`${readingLabel} Finish`}
                      value={
                        endReading ||
                        'Not recorded'
                      }
                    />
                  </div>
                </section>
              ) : null}

              <section style={styles.formCard}>
                <h2 style={styles.sectionHeading}>
                  Aircraft after flight
                </h2>

                <div className="pilot-post-flight__detail-grid" style={styles.detailGrid}>
                  <ReadOnlyItem
                    label="Fuel added"
                    value={`${fuelAdded || '0'} L`}
                  />
                  <ReadOnlyItem
                    label="Oil added"
                    value={`${oilAdded || '0'} L`}
                  />
                  <ReadOnlyItem
                    label="Aircraft condition"
                    value={savedAircraftCondition}
                  />
                  <ReadOnlyItem
                    label="Defects reported"
                    value={
                      hasRecordedDefect(
                        savedSubmission
                      ) || defect
                        ? defect ||
                          savedSubmission.defect ||
                          savedSubmission.snag ||
                          'Reported'
                        : 'None'
                    }
                  />
                </div>
              </section>

              <section style={styles.formCard}>
                <h2 style={styles.sectionHeading}>
                  Pilot remarks
                </h2>

                <div style={styles.readOnlyTextBlock}>
                  {remarks ||
                    savedSubmission.remarks ||
                    savedSubmission.notes ||
                    'No remarks recorded.'}
                </div>
              </section>

              {Array.isArray(
                savedSubmission.exceptionReasons
              ) &&
              savedSubmission.exceptionReasons.length > 0 ? (
                <section style={styles.infoCard}>
                  <div style={styles.infoCardTitle}>
                    Submission notes
                  </div>

                  <ul style={styles.infoList}>
                    {savedSubmission.exceptionReasons.map(
                      (reason) => (
                        <li key={reason}>{reason}</li>
                      )
                    )}
                  </ul>
                </section>
              ) : null}
            </>
          ) : (
            <>
              <section style={styles.formCard}>
                <h2 style={styles.sectionHeading}>
                  This flight
                </h2>

                <div className="pilot-post-flight__form-grid" style={styles.formGrid}>
                  <Field
                    label="Actual departure"
                    type="time"
                    value={
                      actualDepartureTime
                    }
                    onChange={
                      setActualDepartureTime
                    }
                  />

                  <Field
                    label="Actual arrival"
                    type="time"
                    value={
                      actualArrivalTime
                    }
                    onChange={
                      setActualArrivalTime
                    }
                  />
                </div>

                <div className="pilot-post-flight__form-grid" style={styles.formGrid}>
                  <Field
                    label="Actual departure airfield"
                    type="text"
                    value={
                      actualDepartureAirfield
                    }
                    onChange={(value) =>
                      setActualDepartureAirfield(
                        value.toUpperCase()
                      )
                    }
                    placeholder="e.g. EGTR"
                  />

                  <Field
                    label="Actual arrival airfield"
                    type="text"
                    value={
                      actualArrivalAirfield
                    }
                    onChange={(value) =>
                      setActualArrivalAirfield(
                        value.toUpperCase()
                      )
                    }
                    placeholder="e.g. EGTO"
                  />
                </div>

                <div style={styles.flightTimeRow}>
                  <span style={styles.mutedLabel}>
                    Flight time
                  </span>

                  <strong style={styles.flightTime}>
                    {flightTime || '—'}
                  </strong>
                </div>
              </section>

              <section style={styles.formCard}>
                <h2 style={styles.sectionHeading}>
                  Aircraft after flight
                </h2>

                {usesMeter ? (
                  <>
                    <div className="pilot-post-flight__form-grid" style={styles.formGrid}>
                      <Field
                        label={`${readingLabel} Start`}
                        type="number"
                        value={startReading}
                        onChange={setStartReading}
                        placeholder="Enter reading"
                        step="0.1"
                      />

                      <Field
                        label={`${readingLabel} Finish`}
                        type="number"
                        value={endReading}
                        onChange={setEndReading}
                        placeholder="Enter reading"
                        step="0.1"
                      />
                    </div>

                    {endBeforeStart ? (
                      <div style={styles.inlineWarning}>
                        {readingLabel} Finish cannot
                        be lower than{' '}
                        {readingLabel} Start.
                      </div>
                    ) : null}
                  </>
                ) : null}

                <div className="pilot-post-flight__change-list" style={styles.changeList}>
                  <ExpandableAction
                    label="Added fuel"
                    expanded={fuelExpanded}
                    onToggle={() => {
                      setFuelExpanded(
                        (previous) => !previous
                      );

                      if (fuelExpanded) {
                        setFuelAdded('');
                      }
                    }}
                  >
                    <Field
                      label="Fuel added (litres)"
                      type="number"
                      value={fuelAdded}
                      onChange={setFuelAdded}
                      placeholder="Enter litres"
                      step="0.1"
                    />
                  </ExpandableAction>

                  <ExpandableAction
                    label="Added oil"
                    expanded={oilExpanded}
                    onToggle={() => {
                      setOilExpanded(
                        (previous) => !previous
                      );

                      if (oilExpanded) {
                        setOilAdded('');
                      }
                    }}
                  >
                    <Field
                      label="Oil added (litres)"
                      type="number"
                      value={oilAdded}
                      onChange={setOilAdded}
                      placeholder="Enter litres"
                      step="0.1"
                    />
                  </ExpandableAction>
                </div>

                <div style={styles.conditionBlock}>
                  <div style={styles.controlLabel}>
                    Aircraft condition
                  </div>

                  <div className="pilot-post-flight__condition-options" style={styles.conditionOptions}>
                    <button
                      type="button"
                      onClick={() => {
                        setNoDefects(true);
                        setDefect('');
                      }}
                      style={{
                        ...styles.conditionButton,

                        ...(noDefects
                          ? styles.conditionButtonSelected
                          : {}),
                      }}
                    >
                      Serviceable
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setNoDefects(false)
                      }
                      style={{
                        ...styles.conditionButton,

                        ...(!noDefects
                          ? styles.conditionButtonWarning
                          : {}),
                      }}
                    >
                      Report Defect
                    </button>
                  </div>

                  {!noDefects ? (
                    <label style={styles.label}>
                      <span>
                        Describe the defect or snag
                      </span>

                      <textarea
                        rows={3}
                        value={defect}
                        onChange={(event) =>
                          setDefect(
                            event.target.value
                          )
                        }
                        placeholder="Describe what you found."
                        style={styles.textarea}
                      />
                    </label>
                  ) : (
                    <div
                      style={
                        styles.serviceableNotice
                      }
                    >
                      No aircraft defects reported.
                    </div>
                  )}
                </div>

                <ExpandableAction
                  label="Add pilot note"
                  expanded={noteExpanded}
                  onToggle={() => {
                    setNoteExpanded(
                      (previous) => !previous
                    );

                    if (noteExpanded) {
                      setRemarks('');
                    }
                  }}
                >
                  <label style={styles.label}>
                    <span>Notes for the club</span>

                    <textarea
                      rows={3}
                      value={remarks}
                      onChange={(event) =>
                        setRemarks(
                          event.target.value
                        )
                      }
                      placeholder="For example: refuelled to tabs, aircraft cleaned or left in another hangar."
                      style={styles.textarea}
                    />
                  </label>
                </ExpandableAction>
              </section>

              {saveError ? (
                <div style={styles.errorCard}>
                  {saveError}
                </div>
              ) : null}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={
                  !canSubmit || saving || noFlightSaving
                }
                style={{
                  ...ds.buttons.primary,
                  ...styles.completeButton,

                  opacity:
                    canSubmit && !saving
                      ? 1
                      : 0.5,

                  cursor:
                    canSubmit && !saving
                      ? 'pointer'
                      : 'not-allowed',
                }}
              >
                {saving
                  ? 'Closing flight...'
                  : 'Close Flight'}
              </button>

              <button
                type="button"
                onClick={() => setNoFlightModalOpen(true)}
                disabled={saving || noFlightSaving}
                style={{
                  ...ds.buttons.secondary,
                  width: '100%',
                  justifyContent: 'center',
                  marginTop: '0.55rem',
                  opacity: saving || noFlightSaving ? 0.55 : 1,
                  cursor: saving || noFlightSaving ? 'not-allowed' : 'pointer',
                }}
              >
                Flight did not take place
              </button>

              <div style={styles.notice}>
                Routine entries are added to the
                aircraft technical log
                automatically. Defects and
                discrepancies are flagged for the
                club.
              </div>
            </>
          )}
        </>
      ) : null}

      {noFlightModalOpen ? (
        <div style={styles.modalBackdrop} role="presentation">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="did-not-fly-heading"
            className="pilot-post-flight__modal"
            style={styles.modalCard}
          >
            <h2 id="did-not-fly-heading" style={styles.modalTitle}>
              Flight did not take place?
            </h2>

            <p style={styles.modalText}>
              This will close the booking without recording flight time, aircraft readings, fuel, oil or a logbook entry.
            </p>

            <div className="pilot-post-flight__modal-actions" style={styles.modalActions}>
              <button
                type="button"
                onClick={handleDidNotFlyConfirm}
                disabled={noFlightSaving}
                style={{
                  ...ds.buttons.secondary,
                  ...styles.modalConfirmButton,
                  opacity: noFlightSaving ? 0.6 : 1,
                }}
              >
                {noFlightSaving ? 'Updating...' : 'Confirm no flight'}
              </button>

              <button
                type="button"
                onClick={() => setNoFlightModalOpen(false)}
                disabled={noFlightSaving}
                style={{
                  ...ds.buttons.ghost,
                  ...styles.modalCancelButton,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ReadOnlyItem({
  label,
  value,
}) {
  return (
    <div style={styles.detailItem}>
      <div style={styles.detailLabel}>
        {label}
      </div>

      <div style={styles.detailValue}>
        {value || 'Not recorded'}
      </div>
    </div>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  placeholder,
  step,
}) {
  return (
    <label style={styles.label}>
      <span>{label}</span>

      <input
        type={type}
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        placeholder={placeholder}
        step={step}
        style={styles.input}
      />
    </label>
  );
}

function ExpandableAction({
  label,
  expanded,
  onToggle,
  children,
}) {
  return (
    <div style={styles.expandableBlock}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          ...styles.expandableButton,

          ...(expanded
            ? styles.expandableButtonActive
            : {}),
        }}
      >
        <span>
          {expanded ? '−' : '+'}
        </span>

        <span>{label}</span>
      </button>

      {expanded ? (
        <div style={styles.expandedContent}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 760,
    margin: '2rem auto',
    padding: '0 1rem 2rem',
    color: '#e8edf2',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap',
    marginBottom: '0.85rem',
  },

  title: {
    margin: 0,
    color: '#f3f5f7',
    fontSize: '1.3rem',
    fontWeight: 650,
  },

  subtitle: {
    marginTop: 4,
    color: '#96a0aa',
    fontSize: '0.8rem',
  },

  messageCard: {
    padding: '0.9rem',
    borderRadius: 9,
    border: '1px solid #343a42',
    background: '#191d22',
    color: '#a7b0b9',
  },

  errorCard: {
    marginBottom: '0.7rem',
    padding: '0.7rem',
    borderRadius: 8,
    border:
      '1px solid rgba(210,100,100,0.42)',
    background:
      'rgba(130,42,42,0.16)',
    color: '#efb0b0',
    fontSize: '0.76rem',
  },

  noFlightTitle: {
    color: '#edf0f3',
    fontWeight: 650,
  },

  noFlightText: {
    marginTop: 4,
    fontSize: '0.8rem',
  },

  summaryCard: {
    padding: '0.8rem',
    borderRadius: 9,
    border: '1px solid #343a42',
    background: '#191d22',
    marginBottom: '0.65rem',
  },

  aircraftSummary: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },

  aircraftRegistration: {
    color: '#f2f4f6',
    fontSize: '1.05rem',
    fontWeight: 700,
  },

  aircraftRoute: {
    marginTop: 4,
    color: '#aab3bc',
    fontSize: '0.76rem',
  },

  bookingSummary: {
    textAlign: 'right',
  },

  statusCard: {
    padding: '0.8rem',
    borderRadius: 9,
    border:
      '1px solid rgba(89,173,119,0.36)',
    background:
      'rgba(32,74,46,0.20)',
    marginBottom: '0.65rem',
  },

  statusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },

  statusKicker: {
    color: '#9fd7ae',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },

  statusTitle: {
    color: '#eef5f0',
    fontSize: '1rem',
    fontWeight: 700,
    marginTop: '0.25rem',
  },

  statusMeta: {
    color: '#b7c8bc',
    fontSize: '0.76rem',
    marginTop: '0.3rem',
  },

  completedBadge: {
    color: '#d8f2df',
    background: 'rgba(55,214,122,0.16)',
    border:
      '1px solid rgba(55,214,122,0.28)',
    borderRadius: 999,
    padding: '0.35rem 0.65rem',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },

  bookingDate: {
    color: '#e0e4e8',
    fontSize: '0.76rem',
    fontWeight: 600,
  },

  bookingTime: {
    marginTop: 4,
    color: '#8f99a3',
    fontSize: '0.68rem',
  },

  continuityCard: {
    display: 'grid',
    gap: '0.3rem',
    padding: '0.7rem 0.8rem',
    borderRadius: 9,
    border: '1px solid #39434c',
    background: '#191d22',
    marginBottom: '0.65rem',
  },

  continuityWarning: {
    display: 'grid',
    gap: '0.35rem',
    padding: '0.7rem 0.8rem',
    borderRadius: 9,
    border:
      '1px solid rgba(196,143,71,0.54)',
    background:
      'rgba(128,84,28,0.15)',
    marginBottom: '0.65rem',
  },

  continuityTitle: {
    color: '#dce1e6',
    fontSize: '0.74rem',
    fontWeight: 650,
  },

  continuityText: {
    color: '#9da7b0',
    fontSize: '0.7rem',
    lineHeight: 1.4,
  },

  warningText: {
    color: '#ddb979',
    fontSize: '0.7rem',
    lineHeight: 1.4,
  },

  inlineWarning: {
    marginTop: '0.6rem',
    padding: '0.5rem',
    borderRadius: 6,
    border:
      '1px solid rgba(196,143,71,0.42)',
    background:
      'rgba(128,84,28,0.12)',
    color: '#ddb979',
    fontSize: '0.7rem',
  },

  formCard: {
    padding: '0.8rem',
    borderRadius: 9,
    border: '1px solid #343a42',
    background: '#191d22',
    marginBottom: '0.65rem',
  },

  sectionHeading: {
    margin: '0 0 0.65rem',
    color: '#dce1e6',
    fontSize: '0.78rem',
    fontWeight: 650,
  },

  formGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.6rem',
  },

  detailGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.65rem',
  },

  detailItem: {
    padding: '0.7rem 0.75rem',
    borderRadius: 8,
    border: '1px solid #343a42',
    background: '#111419',
    minWidth: 0,
  },

  detailLabel: {
    color: '#8f99a3',
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },

  detailValue: {
    color: '#edf0f3',
    fontSize: '0.82rem',
    fontWeight: 600,
    lineHeight: 1.45,
    marginTop: '0.3rem',
    wordBreak: 'break-word',
  },

  readOnlyTextBlock: {
    padding: '0.75rem 0.8rem',
    borderRadius: 8,
    border: '1px solid #343a42',
    background: '#111419',
    color: '#edf0f3',
    fontSize: '0.82rem',
    lineHeight: 1.55,
    whiteSpace: 'pre-wrap',
  },

  infoCard: {
    padding: '0.8rem',
    borderRadius: 9,
    border:
      '1px solid rgba(196,143,71,0.36)',
    background:
      'rgba(128,84,28,0.12)',
    marginBottom: '0.65rem',
  },

  infoCardTitle: {
    color: '#f0d3a3',
    fontSize: '0.75rem',
    fontWeight: 700,
    marginBottom: '0.45rem',
  },

  infoList: {
    margin: 0,
    paddingLeft: '1.1rem',
    color: '#e7d7b8',
    fontSize: '0.76rem',
    lineHeight: 1.5,
  },

  label: {
    display: 'grid',
    gap: 5,
    color: '#aab2ba',
    fontSize: '0.7rem',
    fontWeight: 600,
  },

  input: {
    width: '100%',
    padding: '0.55rem 0.6rem',
    borderRadius: 6,
    border: '1px solid #3d454e',
    background: '#111419',
    color: '#edf0f3',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },

  textarea: {
    width: '100%',
    padding: '0.6rem',
    borderRadius: 6,
    border: '1px solid #3d454e',
    background: '#111419',
    color: '#edf0f3',
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    lineHeight: 1.45,
  },

  flightTimeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '0.65rem',
    paddingTop: '0.6rem',
    borderTop: '1px solid #30363d',
  },

  mutedLabel: {
    color: '#858f99',
    fontSize: '0.7rem',
  },

  flightTime: {
    color: '#e7ebef',
    fontSize: '0.82rem',
  },

  changeList: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(2, minmax(0, 1fr))',
    gap: '0.5rem',
    marginTop: '0.7rem',
  },

  expandableBlock: {
    minWidth: 0,
  },

  expandableButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem',
    padding: '0.55rem 0.65rem',
    borderRadius: 7,
    border: '1px solid #3b424a',
    background: '#14181d',
    color: '#bac2ca',
    fontFamily: 'inherit',
    fontSize: '0.72rem',
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
  },

  expandableButtonActive: {
    border: '1px solid #57636e',
    background: '#20262c',
    color: '#e0e5ea',
  },

  expandedContent: {
    paddingTop: '0.55rem',
  },

  conditionBlock: {
    marginTop: '0.8rem',
    paddingTop: '0.7rem',
    borderTop: '1px solid #30363d',
  },

  controlLabel: {
    marginBottom: '0.5rem',
    color: '#aab2ba',
    fontSize: '0.7rem',
    fontWeight: 600,
  },

  conditionOptions: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(2, minmax(0, 1fr))',
    gap: '0.5rem',
    marginBottom: '0.65rem',
  },

  conditionButton: {
    padding: '0.6rem',
    borderRadius: 7,
    border: '1px solid #414850',
    background: '#12161b',
    color: '#bac2ca',
    fontFamily: 'inherit',
    fontSize: '0.74rem',
    fontWeight: 600,
    cursor: 'pointer',
  },

  conditionButtonSelected: {
    color: '#b9d9c1',
    border:
      '1px solid rgba(112,165,126,0.55)',
    background:
      'rgba(72,120,84,0.2)',
  },

  conditionButtonWarning: {
    color: '#e3bd79',
    border:
      '1px solid rgba(191,139,69,0.55)',
    background:
      'rgba(150,103,43,0.18)',
  },

  serviceableNotice: {
    color: '#98a2ab',
    fontSize: '0.7rem',
  },

  completeButton: {
    width: '100%',
    justifyContent: 'center',
    marginTop: '0.1rem',
  },

  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    background: 'rgba(6,10,16,0.72)',
    backdropFilter: 'blur(6px)',
    zIndex: 50,
  },

  modalCard: {
    width: 'min(520px, 100%)',
    borderRadius: 16,
    padding: '1.05rem 1rem 0.95rem',
    border: '1px solid rgba(182,234,255,0.18)',
    background: '#11161e',
    boxShadow: '0 20px 46px rgba(0,0,0,0.34)',
  },

  modalTitle: {
    margin: 0,
    color: '#f3f5f7',
    fontSize: '1.05rem',
    fontWeight: 750,
  },

  modalText: {
    margin: '0.65rem 0 0',
    color: '#a4afba',
    fontSize: '0.88rem',
    lineHeight: 1.5,
  },

  modalActions: {
    display: 'flex',
    gap: '0.7rem',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    marginTop: '1rem',
  },

  modalConfirmButton: {
    minWidth: 170,
    justifyContent: 'center',
  },

  modalCancelButton: {
    minWidth: 120,
    justifyContent: 'center',
  },

  notice: {
    color: '#858f99',
    fontSize: '0.68rem',
    textAlign: 'center',
    marginTop: '0.5rem',
    lineHeight: 1.4,
  },
};